import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Info, ExternalLink } from 'lucide-react';
import { apiService } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import Tooltip from '../common/Tooltip';
import { useProjectStore, useFlowStore } from '../../store';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import type { Queue, WorkflowStep } from '../../types';
import QueueCreationModal from './QueueCreationModal';

/**
 * Configuration for queue_write step
 */
type DataSource = 'previous_step_output' | 'specific_field' | 'specific_step' | 'static';

interface QueueWriteConfig {
  target_queue_id?: number;
  target_queue_name?: string;
  data_source: DataSource;
  source_step_id?: number;
  /** When data_source='specific_field', the name of the field to extract from the previous step's output */
  source_field?: string;
  static_data?: string;
  data_mapping?: Record<string, string>;
  write_mode: 'single' | 'array';
}

interface QueueWriteStepConfigProps {
  step: WorkflowStep;
  projectId: string;
  onConfigChange: (config: QueueWriteConfig) => void;
  disabled?: boolean;
}

interface FormErrors {
  target_queue_id?: string;
  source_step_id?: string;
  source_field?: string;
}

/**
 * QueueWriteStepConfig - Configuration panel for queue_write step type
 *
 * This component allows users to configure how data is written to a queue
 * from within a workflow step.
 *
 * Configuration options:
 * - Target queue selection (required)
 * - Data source: previous_step_output, specific_step, or static
 * - Write mode: single or array
 * - Advanced: data mapping (JSONPath expressions)
 */
const QueueWriteStepConfig: React.FC<QueueWriteStepConfigProps> = ({
  step,
  projectId,
  onConfigChange,
  disabled = false,
}) => {
  // AC-1393: open the selected queue's detail page from the modal so the user
  // can inspect/edit the queue without leaving the workflow editor for long.
  // Uses navigateWithContext so the queue page back-link returns here.
  const { navigateWithContext } = useContextualNavigation();

  // Queue list state
  const [queues, setQueues] = useState<Queue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form state - initialize from step.config or defaults
  const existingConfig = step.config as QueueWriteConfig | undefined;
  // Extract initial queue ID with explicit check for the config object
  const initialQueueId = existingConfig && typeof existingConfig === 'object'
    ? existingConfig.target_queue_id
    : undefined;
  const [targetQueueId, setTargetQueueId] = useState<number | undefined>(initialQueueId);
  const [dataSource, setDataSource] = useState<DataSource>(
    existingConfig?.data_source || 'previous_step_output'
  );
  const [sourceStepId, setSourceStepId] = useState<number | undefined>(
    existingConfig?.source_step_id
  );
  const [sourceField, setSourceField] = useState<string>(
    existingConfig?.source_field || ''
  );
  const [staticData, setStaticData] = useState<string>(
    existingConfig?.static_data || ''
  );
  const [writeMode, setWriteMode] = useState<'single' | 'array'>(
    existingConfig?.write_mode || 'single'
  );
  const [dataMapping, setDataMapping] = useState<string>(
    existingConfig?.data_mapping ? JSON.stringify(existingConfig.data_mapping, null, 2) : ''
  );

  // UI state
  const [errors, setErrors] = useState<FormErrors>({});
  const [showAdvanced, setShowAdvanced] = useState(
    !!(existingConfig?.data_mapping && Object.keys(existingConfig.data_mapping).length > 0)
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Debounce ref for config changes
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastConfigRef = useRef<string>('');
  const isInitialMount = useRef(true);

  // Trace the previous step's agent's output model so we can show a typed
  // field picker. If anything in the chain is missing (no previous step,
  // no agent, no output_model, no properties) we fall back to a text input.
  //
  // AC-1412 / AC-1377 P5.1: project agents and models flow through React
  // Query (`useAgents` / `useModels`) instead of `useProjectStore.agents` /
  // `useProjectStore.models`. The currentProject selection still lives in
  // the project store; only the server-state slices were migrated.
  const currentProjectId = useProjectStore((state) => state.currentProject?.project_id);
  const agentsQuery = useAgents(currentProjectId);
  const modelsQuery = useModels(currentProjectId);
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const { nodes } = useFlowStore();

  /**
   * Resolve the immediate previous step from the flow graph.
   * Returns undefined if no previous step is wired.
   */
  const getStepFromNodes = (stepId: number | string | null | undefined): WorkflowStep | undefined => {
    if (!stepId) return undefined;
    const node = nodes.find((n) => String(n.id) === String(stepId));
    return node?.data?.step as WorkflowStep | undefined;
  };

  const immediatePreviousStep = useMemo<WorkflowStep | undefined>(() => {
    return getStepFromNodes(step.previous_step_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.previous_step_id, nodes]);

  /**
   * Walk past passthrough upstream steps (depth cap 5) to find the first real
   * producer -- the data source for passthrough semantics. Both conditional
   * (AC-1247) and filter (AC-1308) steps emit an envelope carrying the
   * producer's payload under the 'data' key, so both must be skipped to
   * surface the run_agent whose output_model powers the field picker.
   * Returns null if the cap is exceeded or the chain breaks before a producer.
   */
  const PASSTHROUGH_STEP_TYPES = new Set<string>(['conditional', 'filter']);

  const resolveRealUpstreamStep = (
    startStep: WorkflowStep | undefined,
    depth = 5,
  ): WorkflowStep | null => {
    let current: WorkflowStep | undefined = startStep;
    for (let i = 0; i < depth; i++) {
      if (!current) return null;
      if (!PASSTHROUGH_STEP_TYPES.has(current.step_type)) return current;
      const prev = getStepFromNodes(current.previous_step_id);
      if (!prev) return null;
      current = prev;
    }
    return null;
  };

  const realUpstreamStep = useMemo<WorkflowStep | null>(() => {
    return resolveRealUpstreamStep(immediatePreviousStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediatePreviousStep, nodes]);

  const previousStepFields = useMemo<Array<{ name: string; type: string; isList: boolean }>>(() => {
    // Use realUpstreamStep so a conditional between queue_write and the real
    // producer is transparently walked past (passthrough semantics).
    const prevStep = realUpstreamStep;
    if (!prevStep || !prevStep.agent_id) return [];
    const agent = agents.find((a) => a.agent_id === prevStep.agent_id);
    if (!agent?.output_model_id) return [];
    const model = models.find((m) => m.model_id === agent.output_model_id);
    if (!model?.properties) return [];

    return Object.entries(model.properties).map(([name, type]) => {
      // AC-1251: `type` may be either a string shorthand or a rich object.
      const typeStr =
        typeof type === 'string' ? type : String((type as any)?.type ?? '');
      return {
        name,
        type: typeStr,
        isList: typeStr.startsWith('list['),
      };
    });
  }, [realUpstreamStep, agents, models]);

  // Auto-set write_mode when the user picks a field of known type
  const handleSourceFieldChange = (fieldName: string) => {
    setSourceField(fieldName);
    setErrors((prev) => ({ ...prev, source_field: undefined }));
    const matched = previousStepFields.find((f) => f.name === fieldName);
    if (matched) {
      setWriteMode(matched.isList ? 'array' : 'single');
    }
  };

  // Fetch queues function
  const fetchQueues = useCallback(async () => {
    // Skip fetching if no projectId
    if (!projectId) {
      setIsLoading(false);
      setLoadError('Project ID is required');
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const queueList = await apiService.getQueues(projectId);
      setQueues(queueList);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch queues';
      if (message.toLowerCase().includes('timeout')) {
        setLoadError('Request took too long. Please try again.');
      } else {
        setLoadError(`Error: ${message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Fetch queues on mount
  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  // Sync state with step.config when step changes (e.g., when editing different steps)
  // This ensures the form reflects the current step's configuration
  // Dependencies include both step ID and config to handle rerenders with different configs
  const configJson = JSON.stringify(step.config || {});
  useEffect(() => {
    const config = step.config as QueueWriteConfig | undefined;
    if (config) {
      setTargetQueueId(config.target_queue_id);
      setDataSource(config.data_source || 'previous_step_output');
      setSourceStepId(config.source_step_id);
      setSourceField(config.source_field || '');
      setStaticData(config.static_data || '');
      setWriteMode(config.write_mode || 'single');
      setDataMapping(config.data_mapping ? JSON.stringify(config.data_mapping, null, 2) : '');
      // Reset initial mount flag to prevent triggering onConfigChange for this sync
      isInitialMount.current = true;
    }
  }, [step.orchestration_step_id, configJson]);

  // Get selected queue info
  const selectedQueue = queues.find(q => q.queue_id === targetQueueId);
  const isCircuitOpen = selectedQueue?.circuit_breaker_state === 'OPEN';

  // Debounced config change handler
  const notifyConfigChange = useCallback((config: QueueWriteConfig) => {
    const configStr = JSON.stringify(config);
    if (configStr === lastConfigRef.current) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      lastConfigRef.current = configStr;
      onConfigChange(config);
    }, 300);
  }, [onConfigChange]);

  // Build and notify config on form changes
  useEffect(() => {
    const config: QueueWriteConfig = {
      target_queue_id: targetQueueId,
      target_queue_name: targetQueueId ? queues.find(q => q.queue_id === targetQueueId)?.name : undefined,
      data_source: dataSource,
      write_mode: writeMode,
    };

    if (dataSource === 'specific_step' && sourceStepId) {
      config.source_step_id = sourceStepId;
    }

    if (dataSource === 'specific_field' && sourceField) {
      config.source_field = sourceField;
    }

    if (dataSource === 'static' && staticData) {
      config.static_data = staticData;
    }

    if (dataMapping) {
      try {
        config.data_mapping = JSON.parse(dataMapping);
      } catch {
        // Invalid JSON, don't include
      }
    }

    // Don't call onConfigChange on initial mount when displaying existing config
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastConfigRef.current = JSON.stringify(config);
      return;
    }

    notifyConfigChange(config);
  }, [targetQueueId, queues, dataSource, sourceStepId, sourceField, staticData, writeMode, dataMapping, notifyConfigChange]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Validate form

  // Handle queue selection
  const handleQueueChange = (value: string) => {
    const queueId = value ? parseInt(value, 10) : undefined;
    setTargetQueueId(queueId);
    if (queueId) {
      setErrors(prev => ({ ...prev, target_queue_id: undefined }));
    }
  };

  // Handle data source change
  const handleDataSourceChange = (value: DataSource) => {
    setDataSource(value);
    if (value !== 'specific_step') {
      setErrors(prev => ({ ...prev, source_step_id: undefined }));
    }
    if (value !== 'specific_field') {
      setErrors(prev => ({ ...prev, source_field: undefined }));
    }
  };

  /**
   * Banner override (AC-1247): the user clicks "Use <agent> directly" and we
   * switch the data source to specific_step, pre-filling source_step_id with
   * the real upstream producer's ID so they bypass the conditional passthrough
   * entirely. Keeps the explicit user choice visible in the form.
   *
   * This is an explicit, discrete user action, so we bypass the 300ms typing
   * debounce and notify the parent immediately.
   */
  const handleUseRealUpstreamDirectly = () => {
    if (!realUpstreamStep?.orchestration_step_id) return;
    // orchestration_step_id is typed as string on WorkflowStep but fixtures
    // use numeric IDs; normalize to number for the state + config.
    const rawId = realUpstreamStep.orchestration_step_id as unknown as number | string;
    const upstreamStepId = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
    if (!upstreamStepId || Number.isNaN(upstreamStepId)) return;

    setDataSource('specific_step');
    setSourceStepId(upstreamStepId);
    setErrors(prev => ({ ...prev, source_step_id: undefined }));

    // Build and dispatch the new config immediately so consumers (and tests)
    // see the change without waiting for the debounced useEffect.
    const overrideConfig: QueueWriteConfig = {
      target_queue_id: targetQueueId,
      target_queue_name: targetQueueId ? queues.find(q => q.queue_id === targetQueueId)?.name : undefined,
      data_source: 'specific_step',
      source_step_id: upstreamStepId,
      write_mode: writeMode,
    };
    if (dataMapping) {
      try {
        overrideConfig.data_mapping = JSON.parse(dataMapping);
      } catch {
        // Invalid JSON, don't include
      }
    }
    lastConfigRef.current = JSON.stringify(overrideConfig);
    onConfigChange(overrideConfig);
  };

  // Whether to render the passthrough banner. Both conditional and filter
  // steps pass the real producer's payload through via the envelope's data
  // key, so the banner applies to both (AC-1247, AC-1308).
  const showPassthroughBanner =
    !!immediatePreviousStep &&
    PASSTHROUGH_STEP_TYPES.has(immediatePreviousStep.step_type) &&
    (dataSource === 'previous_step_output' || dataSource === 'specific_field');
  const passthroughStepLabel =
    immediatePreviousStep?.step_type === 'filter' ? 'filter' : 'conditional';

  // Handle write mode change
  const handleWriteModeChange = (value: 'single' | 'array') => {
    setWriteMode(value);
  };

  // Handle queue created from modal
  const handleQueueCreated = async (newQueue: Queue) => {
    // Close the modal
    setIsModalOpen(false);

    // Refresh the queue list
    await fetchQueues();

    // Auto-select the newly created queue
    setTargetQueueId(newQueue.queue_id);

    // Clear any target_queue_id errors
    setErrors(prev => ({ ...prev, target_queue_id: undefined }));
  };

  // Don't show loading state - render form with loading indicator and disabled fields instead
  // This allows tests to access form elements even while loading
  const isFormDisabled = disabled || isLoading;

  // Render error state
  if (loadError) {
    return (
      <div className="p-4">
        <p className="text-red-600 dark:text-red-400">{loadError}</p>
      </div>
    );
  }

  // Render empty state (only if not loading)
  if (!isLoading && queues.length === 0) {
    return (
      <div className="p-4">
        <p className="text-yellow-600 dark:text-yellow-400">
          No queues available. Please create a queue first.
        </p>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          data-testid="create-new-queue-link"
          className="mt-2 text-primary-600 hover:text-primary-800 text-sm font-medium"
        >
          + Create New Queue
        </button>
        <QueueCreationModal
          projectId={projectId}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onQueueCreated={handleQueueCreated}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Loading indicator */}
      {isLoading && (
        <div className="p-3 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-md">
          <p className="text-sm text-blue-800 dark:text-blue-300">Loading queues...</p>
        </div>
      )}

      {/* Help text */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Writes data to a queue. This step adds items to the specified queue for later processing.
      </p>

      {/* Target Queue Selection */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <label htmlFor="target-queue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Target Queue
            </label>
            {targetQueueId && (
              <Tooltip content="Open queue page">
                <button
                  type="button"
                  onClick={() =>
                    navigateWithContext(
                      `/projects/${projectId}/queues/${targetQueueId}`,
                      'Workflow',
                    )
                  }
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                  aria-label="Open queue page"
                  data-testid="open-target-queue-link"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            disabled={isFormDisabled}
            data-testid="create-new-queue-link"
            className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Create new queue
          </button>
        </div>
        <CustomSelect
          value={targetQueueId?.toString() || ''}
          onChange={(value) => handleQueueChange(value)}
          options={[
            ...queues.map((queue) => ({
              value: queue.queue_id.toString(),
              label: queue.name,
            })),
            // If we have a selected queue ID but queues haven't loaded yet (isLoading=true and queues.length=0),
            // render a temporary placeholder so the dropdown value can be set during loading
            ...(isLoading && targetQueueId && queues.length === 0
              ? [{ value: targetQueueId.toString(), label: 'Loading...' }]
              : []),
          ]}
          placeholder="Select a queue..."
          disabled={isFormDisabled}
          aria-label="Target Queue"
        />
        {errors.target_queue_id && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.target_queue_id}</p>
        )}
      </div>

      {/* Circuit Breaker Warning */}
      {isCircuitOpen && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            Warning: Circuit breaker is OPEN for this queue. Writes will be blocked until the circuit is reset.
          </p>
        </div>
      )}

      {/*
        Passthrough banner (AC-1247, AC-1308).
        When the immediate previous step is a conditional or filter, the
        backend passes the real producer's payload through the envelope.
        The banner names that producer so the user knows where the data
        actually comes from, and offers a one-click override to bypass the
        intermediate step and bind directly to the producer's output.
      */}
      {showPassthroughBanner && (
        <div
          className="p-3 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-md"
          data-testid="conditional-passthrough-banner"
        >
          <p className="text-sm text-blue-900 dark:text-blue-200">
            <Info className="inline-block w-4 h-4 mr-1 align-text-bottom" />
            Using data from{' '}
            <span className="font-semibold">
              {realUpstreamStep?.label ?? realUpstreamStep?.description ?? `the step before the ${passthroughStepLabel}`}
            </span>{' '}
            (passed through the {passthroughStepLabel}). Change source to override.
          </p>
          {realUpstreamStep?.orchestration_step_id && (
            <button
              type="button"
              onClick={handleUseRealUpstreamDirectly}
              disabled={isFormDisabled}
              className="mt-2 text-xs text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-100 font-medium underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use {realUpstreamStep?.label ?? realUpstreamStep?.description ?? 'upstream step'} directly
            </button>
          )}
        </div>
      )}

      {/* Data Source Selection */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <span id="data-source-label">Data Source</span>
          <Tooltip content="Determines where the data comes from that will be written to the queue.">
            <Info
              className="inline-block ml-1 w-4 h-4 cursor-help text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              aria-label="help"
            />
          </Tooltip>
        </legend>
        <div className="space-y-2" role="radiogroup" aria-labelledby="data-source-label">
          <label className="flex items-center">
            <input
              type="radio"
              name="data-source"
              id="data-source-previous"
              aria-label="Previous Step Output"
              value="previous_step_output"
              checked={dataSource === 'previous_step_output'}
              onChange={() => handleDataSourceChange('previous_step_output')}
              disabled={isFormDisabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Previous Step Output</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="data-source"
              id="data-source-specific-field"
              aria-label="Specific Field from Previous Step"
              value="specific_field"
              checked={dataSource === 'specific_field'}
              onChange={() => handleDataSourceChange('specific_field')}
              disabled={isFormDisabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Specific Field from Previous Step</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="data-source"
              id="data-source-specific"
              aria-label="Specific Step"
              value="specific_step"
              checked={dataSource === 'specific_step'}
              onChange={() => handleDataSourceChange('specific_step')}
              disabled={isFormDisabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Specific Step</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="data-source"
              id="data-source-static"
              aria-label="Static Data"
              value="static"
              checked={dataSource === 'static'}
              onChange={() => handleDataSourceChange('static')}
              disabled={isFormDisabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Static Data</span>
          </label>
        </div>
      </fieldset>

      {/* Source Step Selector (conditional) */}
      {dataSource === 'specific_step' && (
        <div>
          <label htmlFor="source-step" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Source Step
          </label>
          <input
            type="number"
            id="source-step"
            value={sourceStepId || ''}
            onChange={(e) => setSourceStepId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
            disabled={isFormDisabled}
            placeholder="Enter step ID"
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
          />
          {errors.source_step_id && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.source_step_id}</p>
          )}
        </div>
      )}

      {/* Source Field Picker (conditional) */}
      {dataSource === 'specific_field' && (
        <div>
          <label htmlFor="source-field" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Source Field
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Pick which field from the previous step's output to write. List fields write each
            element as a separate item; scalar fields write a single item.
          </p>
          {previousStepFields.length > 0 ? (
            <CustomSelect
              value={sourceField}
              onChange={handleSourceFieldChange}
              options={previousStepFields.map((f) => ({
                value: f.name,
                label: `${f.name} (${f.type})`,
              }))}
              placeholder="Select a field..."
              disabled={isFormDisabled}
              aria-label="Source Field"
            />
          ) : (
            <input
              type="text"
              id="source-field"
              value={sourceField}
              onChange={(e) => handleSourceFieldChange(e.target.value)}
              disabled={isFormDisabled}
              placeholder="e.g., available"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
            />
          )}
          {previousStepFields.length === 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
              Could not detect the previous step's output schema. Type the field name manually.
            </p>
          )}
          {errors.source_field && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.source_field}</p>
          )}
        </div>
      )}

      {/* Static Data Input (conditional) */}
      {dataSource === 'static' && (
        <div>
          <label htmlFor="static-data" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Static Data (JSON Payload)
          </label>
          <textarea
            id="static-data"
            value={staticData}
            onChange={(e) => setStaticData(e.target.value)}
            disabled={isFormDisabled}
            placeholder='{"key": "value"}'
            rows={4}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700 font-mono"
          />
        </div>
      )}

      {/* Write Mode Selection */}
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <span id="write-mode-label">Write Mode</span>
        </legend>
        <div className="space-y-2" role="radiogroup" aria-labelledby="write-mode-label">
          <label className="flex items-center">
            <input
              type="radio"
              name="write-mode"
              id="write-mode-single"
              aria-label="Single item"
              value="single"
              checked={writeMode === 'single'}
              onChange={() => handleWriteModeChange('single')}
              disabled={isFormDisabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Single item</span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">- Write the payload as one queue item</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="write-mode"
              id="write-mode-array"
              aria-label="Split array one item per element"
              value="array"
              checked={writeMode === 'array'}
              onChange={() => handleWriteModeChange('array')}
              disabled={isFormDisabled}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Split array (one item per element)</span>
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">- Iterate the array and write each element as its own queue item</span>
          </label>
        </div>
      </fieldset>

      {/* Advanced Options Toggle */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-primary-600 hover:text-primary-800"
        >
          {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
        </button>
      </div>

      {/* Advanced Options */}
      {showAdvanced && (
        <div>
          <label htmlFor="data-mapping" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Data Mapping (Transform)
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Use JSONPath expressions to transform the data before writing. Example: {`{"email": "$.user.email"}`}
          </p>
          <textarea
            id="data-mapping"
            value={dataMapping}
            onChange={(e) => setDataMapping(e.target.value)}
            disabled={isFormDisabled}
            placeholder='{"field": "$.path.to.value"}'
            rows={3}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700 font-mono"
          />
        </div>
      )}

      {/* Queue Creation Modal */}
      <QueueCreationModal
        projectId={projectId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onQueueCreated={handleQueueCreated}
      />
    </div>
  );
};

export default QueueWriteStepConfig;
